import { useCallback, useEffect, useId, useMemo, useRef } from 'react';
import { useFetcher } from '@remix-run/react';
import { atom, useAtom, useSetAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import type { z } from 'zod';

import { useTracker } from '@archipro-website/tracker';
import type { SubmitActionResponse } from '~/routes/remix-api.enquiry.submit';
import { useAuth } from '@modules/root/context';
import {
    assertErrorResponse,
    assertSuccessResponse,
} from '@modules/root/graphql/responses';
import { useUserLoggedIn } from '@modules/user';
import { usePublicUser } from '@modules/user/hook/use-public-user';
import { trackSubmitCreateEnquiryRequest } from '@modules/tracking/util/trackSubmitCreateEnquiryRequest';
import { trackBrochureRequest } from '@modules/tracking/util/trackBrochureRequest';
import { trackProjectEnquire } from '@modules/tracking/util/trackProjectEnquiry';
import type { ProfessionalInitialResult } from '@modules/professional/hook/use-professional-initial-route-data';
import type { CreateEnquirySource } from '@modules/enquiry';
import {
    getEnquiryFormSchema,
    type enquiryFormSessionSchema,
} from '../validation';
import { trackCreatedEnquiry } from '~/modules/tracking/util/trackCreatedEnquiry';
import { trackAuthEvent } from '~/modules/user/util/track-auth-event';
import { useAtomEvent } from '~/utils-rocco/hooks/use-atom-event';

const ENQUIRY_FETCHER_KEY = 'enquiry';

/**
 * The saved enquiry form session.
 */
const enquirySessionAtom = atomWithStorage<Partial<
    z.infer<typeof enquiryFormSessionSchema>
> | null>(
    'enquiry-form-session',
    null,
    createJSONStorage(() => sessionStorage)
);
/**
 * The ID of the enquiry form. Each `useEnquiry` hook is assigned a unique
 * ID to make sure the post-submission `useEffect` logic is run only for that
 * specific submission. A page may have multiple enquiry forms (e.g. in-page and modal).
 */
const enquiryFormIdAtom = atom<string | null>(null);
/**
 * Controls whether the enquiry form should auto submit after the user logs in.
 */
const enquiryAutoSubmitAtom = atomWithStorage<boolean>(
    'enquiry-form-auto-submit',
    false,
    createJSONStorage(() => sessionStorage)
);
/**
 * The last enquiry form submission was auto-submitted after login.
 */
const isAutoSubmittedAtom = atom(false);
/**
 * We have to wait for the page to refresh after login before we auto submit
 * the enquiry. The refresh after login happens in
 * {@link packages/web/app/modules/root/util/handle-auth-redirect.ts}.
 * This is a workaround to make sure the success animation is visible.
 * TODO: Remove when we don't refresh the page after login.
 */
const autoSubmitDelayAtom = atom(false);

const enquirySuccessAtom = atom<number>(0);

export interface UseEnquiryProps {
    /** The professional to submit an enquiry to. */
    professional: Pick<
        ProfessionalInitialResult,
        'ID' | 'Title' | 'branchOptions'
    >;
    /** The type of the Library Item (professional, product, project, article). */
    itemType: 'professional' | 'product' | 'project' | 'article';
    /** The ID of the Library Item (professional, product, project, article). */
    siteTreeID: number;
    /** The source of the enquiry. */
    source: CreateEnquirySource;
    /** The project details if the enquiry is for a project. Used for tracking. */
    relatedItem?: {
        id: number;
        type: 'project';
        title: string;
        category: string;
    };
    /** Include the branch dropdown if the professional has branches. Defaults to `false`. */
    includeBranch?: boolean;
    /** The enquiry form is in a modal. Defaults to `false`. */
    isModal?: boolean;
    /** Called when the enquiry is successfully submitted. */
    onSuccess?: () => void;
    /** Called when the enquiry fails to submit. */
    onFailure?: () => void;
    /** Called when the user successfully logs in. */
    onAuthSuccess?: () => void;
    /** Called when the user cancels login. */
    onAuthCancel?: () => void;
}

/**
 * Hook for the enquiry form. Contains the form schema, default form values,
 * and functions to submit the enquiry and save the enquiry session.
 *
 * Eventually, move all enquiry form logic to this hook.
 * @see {@link packages/web/app/modules/enquiry/hook/use-enquiry-session.ts}
 */
export function useEnquiry({
    professional,
    itemType,
    siteTreeID,
    source,
    relatedItem,
    includeBranch = false,
    isModal = false,
    onSuccess,
    onFailure,
    onAuthSuccess,
    onAuthCancel,
}: UseEnquiryProps) {
    const id = useId();
    const { isLoggedIn, user } = useUserLoggedIn();
    const { showAuthModal, hideAuthModal } = useAuth();
    const { setPublicUser } = usePublicUser('enquiry');
    const tracker = useTracker();
    const fetcher = useFetcher<SubmitActionResponse>({
        key: ENQUIRY_FETCHER_KEY,
    });
    const [enquirySession, setEnquirySession] = useAtom(enquirySessionAtom);
    const [enquiryFormId, setEnquiryFormId] = useAtom(enquiryFormIdAtom);
    const setEnquiryAutoSubmit = useSetAtom(enquiryAutoSubmitAtom);
    const [isAutoSubmitted, setIsAutoSubmitted] = useAtom(isAutoSubmittedAtom);
    const [autoSubmitDelay, setAutoSubmitDelay] = useAtom(autoSubmitDelayAtom);
    const { triggerSuccess } = useEnquiryEvent({ onSuccess });
    const formSchema = useMemo(
        () =>
            getEnquiryFormSchema(
                !isLoggedIn || !!user.LastName,
                includeBranch && !!professional.branchOptions?.length
            ),
        [isLoggedIn, user, professional, includeBranch]
    );
    const defaultBranchId = useMemo(() => {
        if (!includeBranch || !professional.branchOptions.length) {
            return undefined;
        }
        if (
            enquirySession?.branchId &&
            professional.branchOptions.some(
                (branch) => branch.value.branchId === enquirySession.branchId
            )
        ) {
            return enquirySession.branchId.toString();
        }
        return professional.branchOptions
            .find((branch) => branch.value.branchId === professional.ID)
            ?.value.branchId.toString();
    }, [enquirySession, includeBranch, professional]);
    const defaultFormValues = useMemo(() => {
        return isLoggedIn
            ? {
                  name: `${user.FirstName} ${user.LastName}`.trim(),
                  email: user.Email,
                  phoneNumber:
                      user.Phone || (enquirySession?.phoneNumber ?? ''),
                  postcode: enquirySession?.postcode,
                  branchId: defaultBranchId,
                  message: enquirySession?.message ?? '',
                  files: enquirySession?.files ?? [],
              }
            : {
                  name: enquirySession?.name ?? '',
                  email: enquirySession?.email ?? '',
                  phoneNumber: enquirySession?.phoneNumber ?? '',
                  postcode: enquirySession?.postcode,
                  branchId: defaultBranchId,
                  message: enquirySession?.message ?? '',
                  files: enquirySession?.files ?? [],
              };
    }, [isLoggedIn, user, enquirySession, defaultBranchId]);

    const saveEnquirySession = useCallback(
        (values: Partial<z.infer<typeof formSchema>>, files?: File[]) => {
            const serializableFiles = files?.map((file) => ({
                name: file.name,
                type: file.type,
                size: file.size,
                lastModified: file.lastModified,
            }));

            setEnquirySession({
                name: values.name,
                email: values.email,
                phoneNumber: values.phoneNumber,
                postcode: values.postcode,
                branchId:
                    includeBranch &&
                    values.branchId &&
                    professional.branchOptions.some(
                        (branch) =>
                            branch.value.branchId === Number(values.branchId)
                    )
                        ? Number(values.branchId)
                        : undefined,
                message: values.message,
                siteTreeId: siteTreeID,
                source,
                professional: {
                    ID: professional.ID,
                    Title: professional.Title,
                },
                itemType,
                relatedItem,
                files: serializableFiles,
                isModal,
            });
        },
        [
            includeBranch,
            professional,
            source,
            itemType,
            siteTreeID,
            relatedItem,
            isModal,
            setEnquirySession,
        ]
    );

    const resetEnquirySession = useCallback(
        (messageOnly: boolean = false) => {
            if (messageOnly) {
                setEnquirySession((session) => ({ ...session, message: '' }));
            } else {
                saveEnquirySession({});
            }
            setEnquiryAutoSubmit(false);
        },
        [setEnquirySession, saveEnquirySession, setEnquiryAutoSubmit]
    );

    const submitEnquiry = useCallback(
        (values: z.infer<typeof formSchema>, files?: File[]) => {
            saveEnquirySession(values, files);
            setEnquiryFormId(id);
            setIsAutoSubmitted(false);
            const payload = {
                ...values,
                email: isLoggedIn && user.Email ? user.Email : values.email,
                suburbPostcode: values.postcode.value?.region ?? '',
                locationResponse: JSON.stringify(values.postcode.value),
                siteTreeId: siteTreeID,
                shareDetails: 'true',
                targetProfessionalId: professional.ID,
            };
            const formData = new FormData();
            for (const [key, value] of Object.entries(payload)) {
                if (value !== undefined && value !== null) {
                    formData.append(key, String(value));
                }
            }
            // Add files if present
            if (files?.length) {
                files.forEach((file, index) => {
                    formData.append(`Attachments[${index}]`, file);
                });
            }

            fetcher.submit(formData, {
                method: 'POST',
                action: '/remix-api/enquiry/submit',
                encType: 'multipart/form-data',
            });

            setEnquiryAutoSubmit(false);
            tracker.log('EnquirySubmit', {
                url: new URL(window.location.href),
                label: professional.Title,
            });
            trackSubmitCreateEnquiryRequest(tracker, { source });
            if (itemType === 'professional' || itemType === 'product') {
                trackBrochureRequest(tracker, {
                    type: itemType,
                    region: payload.suburbPostcode,
                });
            } else if (itemType === 'project' && relatedItem) {
                trackProjectEnquire(tracker, {
                    category: relatedItem.category,
                    step: 2,
                    project: relatedItem.title,
                    professional: professional.Title,
                    region: payload.suburbPostcode,
                });
            }
            if (source === 'InPage') {
                tracker.log('OnPageEnquirySend', {
                    url: new URL(window.location.href),
                    targetTracker: 'archiproTracker',
                });
            }
        },
        [
            id,
            fetcher,
            tracker,
            itemType,
            isLoggedIn,
            user,
            professional,
            siteTreeID,
            source,
            relatedItem,
            saveEnquirySession,
            setEnquiryFormId,
            setIsAutoSubmitted,
            setEnquiryAutoSubmit,
        ]
    );

    const myOnSuccess = useCallback(() => {
        trackCreatedEnquiry(tracker, {
            source: enquirySession?.source ?? source,
        });
        if (
            fetcher.data &&
            'data' in fetcher.data &&
            fetcher.data?.data &&
            'GeneratedMember' in fetcher.data?.data &&
            fetcher.data?.data?.GeneratedMember
        ) {
            trackAuthEvent({
                tracker,
                event: 'new_account_registered',
                provider: 'AutoGenerated',
                authSource: 'enquiry',
                guestID: fetcher.data.data.GeneratedMember.TrackedGuest?.ID,
            });
        }
        setEnquirySession((session) => ({ ...session, message: '' }));

        triggerSuccess();
    }, [
        fetcher,
        tracker,
        enquirySession,
        source,
        setEnquirySession,
        triggerSuccess,
    ]);

    const isHandleSubmitComplete = useRef(true);
    useEffect(() => {
        if (
            enquiryFormId !== id &&
            !(!enquirySession?.isModal && isAutoSubmitted)
        ) {
            // Only run for the form that was submitted.
            // If the form is in-page and auto-submitted, run this anyway to
            // show the success animation.
            return;
        } else if (fetcher.state !== 'idle') {
            // Ensure this is only run once per submission.
            isHandleSubmitComplete.current = false;
            return;
        } else if (isHandleSubmitComplete.current || !fetcher.data) {
            return;
        }
        const isSuccess = assertSuccessResponse(fetcher.data);
        const isError = assertErrorResponse(fetcher.data);
        if (isSuccess) {
            myOnSuccess();
        } else if (isError) {
            const publicUser =
                'publicUser' in fetcher.data
                    ? fetcher.data.publicUser
                    : undefined;
            if (publicUser) {
                // If the user exists but is not logged in, auto submit the enquiry
                // form when the user logs in.
                setEnquiryAutoSubmit(true);
                setAutoSubmitDelay(true);
            }
            setPublicUser(publicUser);
            // Prompt login if user is not logged in yet.
            showAuthModal({
                authSource: 'enquiry',
                flow:
                    publicUser?.type === 'new'
                        ? 'post-enquiry-sign-up'
                        : 'post-enquiry-login',
                defaultPublicUser: publicUser,
                onSuccess: () => {
                    hideAuthModal();
                    onAuthSuccess?.();
                },
                onAuthSuccess: () => {
                    hideAuthModal();
                    onAuthSuccess?.();
                },
                onCancel: () => {
                    hideAuthModal();
                    // Re-enable the form if the user cancels login.
                    setAutoSubmitDelay(false);
                    onAuthCancel?.();
                },
            });
            onFailure?.();
        }
        isHandleSubmitComplete.current = true;
    }, [
        id,
        source,
        enquirySession,
        fetcher,
        tracker,
        enquiryFormId,
        isAutoSubmitted,
        resetEnquirySession,
        setEnquiryAutoSubmit,
        setAutoSubmitDelay,
        setPublicUser,
        showAuthModal,
        hideAuthModal,
        onFailure,
        onAuthSuccess,
        onAuthCancel,
        myOnSuccess,
    ]);

    return {
        /**
         * The Zod form schema.
         */
        formSchema,
        /**
         * The default form values.
         */
        defaultFormValues,
        /**
         * The form fetcher state.
         */
        state: fetcher.state,
        /**
         * The form has been submitted and is waiting for the page to refresh
         * before auto submitting the enquiry.
         */
        autoSubmitDelay,
        /**
         * Set the auto submit delay.
         */
        setAutoSubmitDelay,
        /**
         * Submit the enquiry.
         */
        submitEnquiry,
        /**
         * Save the enquiry session to session storage.
         */
        saveEnquirySession,
        /**
         * Reset the enquiry session.
         */
        resetEnquirySession,
    };
}

/**
 * Hook to get the current session and state of the enquiry form.
 */
export function useEnquirySession() {
    const fetcher = useFetcher<SubmitActionResponse>({
        key: ENQUIRY_FETCHER_KEY,
    });
    const [enquirySession, setEnquirySession] = useAtom(enquirySessionAtom);
    const [enquiryFormId, setEnquiryFormId] = useAtom(enquiryFormIdAtom);
    const [enquiryAutoSubmit, setEnquiryAutoSubmit] = useAtom(
        enquiryAutoSubmitAtom
    );
    const [isAutoSubmitted, setIsAutoSubmitted] = useAtom(isAutoSubmittedAtom);
    const [autoSubmitDelay, setAutoSubmitDelay] = useAtom(autoSubmitDelayAtom);

    return useMemo(() => {
        const isDone = fetcher.state === 'idle' && !!fetcher.data;
        return {
            enquirySession,
            enquiryFormId,
            fetcher,
            state: fetcher.state,
            isDone,
            isSuccess: isDone && assertSuccessResponse(fetcher.data!),
            isError: isDone && assertErrorResponse(fetcher.data!),
            enquiryAutoSubmit,
            isAutoSubmitted,
            autoSubmitDelay,
            setEnquirySession,
            setEnquiryFormId,
            setEnquiryAutoSubmit,
            setIsAutoSubmitted,
            setAutoSubmitDelay,
        };
    }, [
        fetcher,
        enquirySession,
        enquiryFormId,
        enquiryAutoSubmit,
        isAutoSubmitted,
        autoSubmitDelay,
        setEnquirySession,
        setEnquiryFormId,
        setEnquiryAutoSubmit,
        setIsAutoSubmitted,
        setAutoSubmitDelay,
    ]);
}

/**
 * Hook to handle enquiry form success events. Multiple components can use this hook to receive onSuccess event
 * even though the component doesn't submit the enquiry.
 *
 * This hook provides a way to trigger success callbacks when an enquiry is
 * successfully submitted. It uses an atom event system to ensure the success
 * callback is called even if the component unmounts during the submission process.
 *
 * @param onSuccess - Optional callback function to execute when enquiry submission succeeds
 * @returns Object containing triggerSuccess function to manually trigger the success event
 */
export function useEnquiryEvent({ onSuccess }: { onSuccess?: () => void }) {
    const { trigger: triggerSuccess } = useAtomEvent(
        enquirySuccessAtom,
        onSuccess
    );

    return {
        triggerSuccess,
    };
}
